import React from "react";
import { navigateTo } from "../lib/router";
import type { RoutePath } from "../lib/router";
import favicon16 from "../assets/favicons/favicon-16.png";
import privacyPolicyPdf from "../assets/legal/Infou_ICS_Privacy_Policy.pdf";
import termsAndConditionsPdf from "../assets/legal/Infou_ICS_Terms_and_Conditions.pdf";

interface FooterProps {
  onNavigate?: (route: RoutePath) => void;
}

export function Footer({ onNavigate }: FooterProps) {
  const handleLinkClick = (e: React.MouseEvent, route: RoutePath) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate(route);
    } else {
      navigateTo(route);
    }
  };

  const handleSectionClick = (e: React.MouseEvent, section: "about" | "contact") => {
    e.preventDefault();
    const currentPath = window.location.pathname;
    if (currentPath === "/" || currentPath === "/landing") {
      window.location.hash = `#${section}`;
      const element = document.getElementById(`${section}-section`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      if (onNavigate) {
        onNavigate("landing");
      } else {
        navigateTo("landing");
      }
      setTimeout(() => {
        window.location.hash = `#${section}`;
        const element = document.getElementById(`${section}-section`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 350);
    }
  };

  return (
    <footer className="bg-zinc-50/50 border-t border-zinc-200 mt-auto">
      <div className="max-w-7xl mx-auto px-6 md:px-20 py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand Info */}
          <div className="md:col-span-1">
            <div className="mb-4 h-12 flex items-center">
              <img src="https://imgh.in/host/5qzegn" alt="Infou Consultancy Services Logo" className="h-full w-auto object-contain" />
            </div>
            <p className="text-zinc-500 font-sans text-sm leading-relaxed max-w-xs">
              Trusted by startups. Supported by innovative leaders
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4 mt-6">
              <a
                href="https://www.linkedin.com/company/infouconsultancyservices/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="text-zinc-400 hover:text-black transition-colors duration-200"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
              {/* <a
                href="https://www.instagram.com/infouconsultancy"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="text-zinc-400 hover:text-black transition-colors duration-200"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
                </svg>
              </a> */}
              {/* <a
                href="https://twitter.com/infouconsultancy"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
                className="text-zinc-400 hover:text-black transition-colors duration-200"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a> */}
            </div>
          </div>

          {/* Legal column */}
          <div>
            <h4 className="font-sans text-xs font-bold tracking-widest uppercase text-black mb-6">
              Legal Framework
            </h4>
            <ul className="space-y-4 font-sans text-sm">
              <li>
                <a
                  href={privacyPolicyPdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-500 hover:text-black transition-colors hover:underline underline-offset-4 decoration-1"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href={termsAndConditionsPdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-500 hover:text-black transition-colors hover:underline underline-offset-4 decoration-1"
                >
                  Terms & Conditions
                </a>
              </li>
            </ul>
          </div>

          {/* Organization column */}
          <div>
            <h4 className="font-sans text-xs font-bold tracking-widest uppercase text-black mb-6">
              Company
            </h4>
            <ul className="space-y-4 font-sans text-sm">
              <li>
                <a
                  href="/#about"
                  onClick={(e) => handleSectionClick(e, "about")}
                  className="text-zinc-500 hover:text-black transition-colors hover:underline underline-offset-4 decoration-1"
                >
                  About Us
                </a>
              </li>
              <li>
                <a
                  href="/services"
                  onClick={(e) => handleLinkClick(e, "services")}
                  className="text-zinc-500 hover:text-black transition-colors hover:underline underline-offset-4 decoration-1"
                >
                  Services
                </a>
              </li>
              <li>
                <a
                  href="/#contact"
                  onClick={(e) => handleSectionClick(e, "contact")}
                  className="text-zinc-500 hover:text-black transition-colors hover:underline underline-offset-4 decoration-1"
                >
                  Contact Us
                </a>
              </li>
            </ul>
          </div>

          {/* Contact and Headquarters */}
          <div>
            <h4 className="font-sans text-xs font-bold tracking-widest uppercase text-black mb-6">
              Office
            </h4>
            <p className="text-zinc-500 font-sans text-sm leading-relaxed mb-6">
              81, 2nd Floor, B Block, Rajat Vihar<br />
              Sector 62, Noida, Uttar Pradesh, 201301<br />
              India
            </p>
            <p className="text-zinc-400 text-xs font-sans">
              © {new Date().getFullYear()} Infou Consultancy Services. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
